import { rootDir } from '@/common/utils/pathPrefix.util';
import { rememberServerTime } from '@/common/utils/serverTime';
import CancellationError from '@/common/utils/errors/CancellationError';
import NetworkError from '@/common/utils/errors/NetworkError';
import JsonParseError from '@/common/utils/errors/JsonParseError';
import NotFoundError from '@/common/utils/errors/NotFoundError';
import ValidationError from '@/common/utils/errors/ValidationError';
import InternalServerError from '@/common/utils/errors/InternalServerError';
import ServerError from '@/common/utils/errors/ServerError';
import { isNil, pickBy, throttle } from 'lodash-es';
import bus from '@/common/utils/eventBus';
import {
  trackNetworkError,
  trackBackendError,
  trackFileUploadStart,
  trackFileUploadComplete
} from '@/common/telemetry';
import extractFileInfo from '@/common/utils/fileInfoExtractor';
import { useSlowRequestsStore } from '@/common/store/pinia/slowRequests';
import BaseHttpAdapter from '@/common/utils/baseHttpAdapter';
import { HTTP_STATUS_NOT_FOUND } from '@/common/constants/httpStatus.constants';

type Config = {
  headers: FetchHeaders | Headers & XAuthorizationHeaders,
  url: string;
  path: string;
  params: any;
  body?: BodyInit | null;
  abortController?: AbortController;
}

type ResponseData = CommonObject | string;

// request headers can carry the MFA receipt and user input; only these go to telemetry
const TRACKED_REQUEST_HEADERS = ['content-type', 'x-requested-with', 'x-session-id'];

export default class CreateFetch extends BaseHttpAdapter {
  // @cpt-algo:cpt-pstorageuihci-algo-create-fetch-construct-uri:p1
  // @cpt-dod:cpt-pstorageuihci-dod-create-fetch-uri-construction:p1
  constructUri(path: UriPath, rootPath: string, params: FetchRequestParams): QueryParamsString {
    // @cpt-begin:cpt-pstorageuihci-algo-create-fetch-construct-uri:p1:inst-base-url
    let url = `${rootPath}${encodeURI(path)}`;
    // @cpt-end:cpt-pstorageuihci-algo-create-fetch-construct-uri:p1:inst-base-url
    // @cpt-begin:cpt-pstorageuihci-algo-create-fetch-construct-uri:p1:inst-has-params
    if (params && Object.keys(params).length > 0) {
      // @cpt-begin:cpt-pstorageuihci-algo-create-fetch-construct-uri:p1:inst-iterate-keys
      const queryParams = Object.keys(params).reduce((result: string[], key: string) => {
        const param = params[key];
        // @cpt-begin:cpt-pstorageuihci-algo-create-fetch-construct-uri:p1:inst-skip-undefined
        if (param === undefined) {
          return result;
        }
        // @cpt-end:cpt-pstorageuihci-algo-create-fetch-construct-uri:p1:inst-skip-undefined
        // @cpt-begin:cpt-pstorageuihci-algo-create-fetch-construct-uri:p1:inst-encode-object
        if (typeof params[key] === 'object') {
          return result.concat(`${key}=${encodeURIComponent(JSON.stringify(param))}`);
        }
        // @cpt-end:cpt-pstorageuihci-algo-create-fetch-construct-uri:p1:inst-encode-object
        // @cpt-begin:cpt-pstorageuihci-algo-create-fetch-construct-uri:p1:inst-encode-scalar
        return result.concat(`${key}=${encodeURIComponent(param)}`);
        // @cpt-end:cpt-pstorageuihci-algo-create-fetch-construct-uri:p1:inst-encode-scalar
      }, []);
      // @cpt-end:cpt-pstorageuihci-algo-create-fetch-construct-uri:p1:inst-iterate-keys
      // @cpt-begin:cpt-pstorageuihci-algo-create-fetch-construct-uri:p1:inst-append-query
      if (queryParams.length) {
        url += `?${queryParams.join('&')}`;
      }
      // @cpt-end:cpt-pstorageuihci-algo-create-fetch-construct-uri:p1:inst-append-query
    }
    // @cpt-end:cpt-pstorageuihci-algo-create-fetch-construct-uri:p1:inst-has-params
    // @cpt-begin:cpt-pstorageuihci-algo-create-fetch-construct-uri:p1:inst-return-url
    return url;
    // @cpt-end:cpt-pstorageuihci-algo-create-fetch-construct-uri:p1:inst-return-url
  }

  // @cpt-algo:cpt-pstorageuihci-algo-create-fetch-get-data:p1
  async getData(response): Promise<ResponseData> {
    // @cpt-begin:cpt-pstorageuihci-algo-create-fetch-get-data:p1:inst-read-text
    const text = await response.text();
    // @cpt-end:cpt-pstorageuihci-algo-create-fetch-get-data:p1:inst-read-text
    let body;
    // @cpt-begin:cpt-pstorageuihci-algo-create-fetch-get-data:p1:inst-read-ct
    const contentType: string = response.headers.get('content-type') ?? '';
    // @cpt-end:cpt-pstorageuihci-algo-create-fetch-get-data:p1:inst-read-ct
    // @cpt-begin:cpt-pstorageuihci-algo-create-fetch-get-data:p1:inst-json-branch
    if (text.length && /application\/json/i.test(contentType)) {
      try {
        // @cpt-begin:cpt-pstorageuihci-algo-create-fetch-get-data:p1:inst-parse-json
        body = JSON.parse(text);
        // @cpt-end:cpt-pstorageuihci-algo-create-fetch-get-data:p1:inst-parse-json
      } catch (err) {
        // @cpt-begin:cpt-pstorageuihci-algo-create-fetch-get-data:p1:inst-throw-parse
        throw new JsonParseError(text, response.status);
        // @cpt-end:cpt-pstorageuihci-algo-create-fetch-get-data:p1:inst-throw-parse
      }
    } else {
      // @cpt-begin:cpt-pstorageuihci-algo-create-fetch-get-data:p1:inst-return-text
      body = text;
      // @cpt-end:cpt-pstorageuihci-algo-create-fetch-get-data:p1:inst-return-text
    }
    // @cpt-end:cpt-pstorageuihci-algo-create-fetch-get-data:p1:inst-json-branch
    return body;
  }

  getContentTypeHeaders(body?: BodyInit | null): { 'Content-Type'?: string } {
    if (isNil(body) || body instanceof FormData) {
      return {};
    }
    return { 'Content-Type': 'application/json' };
  }

  // @cpt-algo:cpt-pstorageuihci-algo-create-fetch-make-request:p1
  // @cpt-state:cpt-pstorageuihci-state-create-fetch-request-lifecycle:p1
  // @cpt-dod:cpt-pstorageuihci-dod-create-fetch-typed-errors:p1
  // @cpt-dod:cpt-pstorageuihci-dod-create-fetch-telemetry-coverage:p1
  // @cpt-dod:cpt-pstorageuihci-dod-create-fetch-cancellation:p1
  // @cpt-dod:cpt-pstorageuihci-dod-create-fetch-auth-and-reconfig-applied:p1
  // @cpt-dod:cpt-pstorageuihci-dod-create-fetch-event-bus-emit:p1
  makeRequest(path: UriPath, { rootPath = rootDir, params = {}, headers = {}, body, ...restOptions }): Promise<FetchResponse> {
    let data;
    // @cpt-begin:cpt-pstorageuihci-algo-create-fetch-make-request:p1:inst-normalize-root
    rootPath = rootPath.replace(/\/$/, '');
    // @cpt-end:cpt-pstorageuihci-algo-create-fetch-make-request:p1:inst-normalize-root
    // @cpt-begin:cpt-pstorageuihci-algo-create-fetch-make-request:p1:inst-build-url
    const url = this.constructUri(path, rootPath, params);
    // @cpt-end:cpt-pstorageuihci-algo-create-fetch-make-request:p1:inst-build-url

    // @cpt-begin:cpt-pstorageuihci-algo-create-fetch-make-request:p1:inst-merge-headers
    const config: Config = {
      url,
      path,
      params,
      body,
      ...restOptions,
      headers: {
        ...this.defaultHeaders,
        ...this.getContentTypeHeaders(body),
        ...headers,
        ...this.getAuthorizationHeaders(restOptions),
      }
    };
    // @cpt-end:cpt-pstorageuihci-algo-create-fetch-make-request:p1:inst-merge-headers

    // @cpt-begin:cpt-pstorageuihci-algo-create-fetch-make-request:p1:inst-make-fetch
    // @cpt-begin:cpt-pstorageuihci-state-create-fetch-request-lifecycle:p1:inst-to-in-flight
    return this.makeFetch(config)
      // @cpt-end:cpt-pstorageuihci-state-create-fetch-request-lifecycle:p1:inst-to-in-flight
      // @cpt-end:cpt-pstorageuihci-algo-create-fetch-make-request:p1:inst-make-fetch
      .catch((err) => {
        // @cpt-begin:cpt-pstorageuihci-algo-create-fetch-make-request:p1:inst-catch-fetch
        // @cpt-begin:cpt-pstorageuihci-algo-create-fetch-make-request:p1:inst-cancel
        // @cpt-begin:cpt-pstorageuihci-state-create-fetch-request-lifecycle:p1:inst-to-canceled
        if (err.name === 'AbortError') {
          throw new CancellationError(err);
        }
        // @cpt-end:cpt-pstorageuihci-state-create-fetch-request-lifecycle:p1:inst-to-canceled
        // @cpt-end:cpt-pstorageuihci-algo-create-fetch-make-request:p1:inst-cancel
        // @cpt-begin:cpt-pstorageuihci-algo-create-fetch-make-request:p1:inst-network-error
        // @cpt-begin:cpt-pstorageuihci-state-create-fetch-request-lifecycle:p1:inst-to-network-failed
        const networkError = new NetworkError(err);
        trackNetworkError(networkError, url, {
          request_method: restOptions.method,
          request_headers: pickBy(config.headers, (_, name) => TRACKED_REQUEST_HEADERS.includes(name.toLowerCase()))
        });
        throw networkError;
        // @cpt-end:cpt-pstorageuihci-state-create-fetch-request-lifecycle:p1:inst-to-network-failed
        // @cpt-end:cpt-pstorageuihci-algo-create-fetch-make-request:p1:inst-network-error
        // @cpt-end:cpt-pstorageuihci-algo-create-fetch-make-request:p1:inst-catch-fetch
      })
      .then(async (res) => {
        rememberServerTime(res.headers.get('date'));
        // @cpt-begin:cpt-pstorageuihci-algo-create-fetch-make-request:p1:inst-get-data
        data = await this.getData(res);
        // @cpt-end:cpt-pstorageuihci-algo-create-fetch-make-request:p1:inst-get-data
        // @cpt-begin:cpt-pstorageuihci-algo-create-fetch-make-request:p1:inst-auth-guard
        // @cpt-begin:cpt-pstorageuihci-state-create-fetch-request-lifecycle:p1:inst-to-auth-failed
        return this.authGuard<Response>(res, data, restOptions as AuthGuardParams);
        // @cpt-end:cpt-pstorageuihci-state-create-fetch-request-lifecycle:p1:inst-to-auth-failed
        // @cpt-end:cpt-pstorageuihci-algo-create-fetch-make-request:p1:inst-auth-guard
      })
      // @cpt-begin:cpt-pstorageuihci-algo-create-fetch-make-request:p1:inst-reconfig-hook
      .then((res) => this.reconfigurationHook<Response>(res))
      // @cpt-end:cpt-pstorageuihci-algo-create-fetch-make-request:p1:inst-reconfig-hook
      .then(async (response) => {
        if (response.status >= 200 && response.status < 400) {
          // @cpt-begin:cpt-pstorageuihci-algo-create-fetch-make-request:p1:inst-success-branch
          // @cpt-begin:cpt-pstorageuihci-state-create-fetch-request-lifecycle:p1:inst-to-ok
          // @cpt-begin:cpt-pstorageuihci-algo-create-fetch-make-request:p1:inst-emit-bus
          if (restOptions.method !== 'get') {
            bus.emit(data);
          }
          // @cpt-end:cpt-pstorageuihci-algo-create-fetch-make-request:p1:inst-emit-bus
          // @cpt-begin:cpt-pstorageuihci-algo-create-fetch-make-request:p1:inst-return-success
          return Promise.resolve({ data, response, request: body, params });
          // @cpt-end:cpt-pstorageuihci-algo-create-fetch-make-request:p1:inst-return-success
          // @cpt-end:cpt-pstorageuihci-state-create-fetch-request-lifecycle:p1:inst-to-ok
          // @cpt-end:cpt-pstorageuihci-algo-create-fetch-make-request:p1:inst-success-branch
        }
        if (response.status >= 400 && response.status < 500) {
          // @cpt-begin:cpt-pstorageuihci-algo-create-fetch-make-request:p1:inst-client-branch
          // @cpt-begin:cpt-pstorageuihci-state-create-fetch-request-lifecycle:p1:inst-to-client-failed
          // @cpt-begin:cpt-pstorageuihci-algo-create-fetch-make-request:p1:inst-not-found
          // @cpt-begin:cpt-pstorageuihci-algo-create-fetch-make-request:p1:inst-validation-error
          const error = response.status === HTTP_STATUS_NOT_FOUND
            ? new NotFoundError(response, data)
            : new ValidationError(response, data);
          // @cpt-end:cpt-pstorageuihci-algo-create-fetch-make-request:p1:inst-validation-error
          // @cpt-end:cpt-pstorageuihci-algo-create-fetch-make-request:p1:inst-not-found

          // @cpt-begin:cpt-pstorageuihci-algo-create-fetch-make-request:p1:inst-track-4xx
          trackBackendError(error, response.status, url, data?.error?.err_code, {
            request_method: restOptions.method,
            response_data: data,
            error_category: '4xx'
          });
          // @cpt-end:cpt-pstorageuihci-algo-create-fetch-make-request:p1:inst-track-4xx

          // @cpt-begin:cpt-pstorageuihci-algo-create-fetch-make-request:p1:inst-throw-4xx
          throw error;
          // @cpt-end:cpt-pstorageuihci-algo-create-fetch-make-request:p1:inst-throw-4xx
          // @cpt-end:cpt-pstorageuihci-state-create-fetch-request-lifecycle:p1:inst-to-client-failed
          // @cpt-end:cpt-pstorageuihci-algo-create-fetch-make-request:p1:inst-client-branch
        }

        // @cpt-begin:cpt-pstorageuihci-algo-create-fetch-make-request:p1:inst-server-branch
        // @cpt-begin:cpt-pstorageuihci-state-create-fetch-request-lifecycle:p1:inst-to-server-failed
        // @cpt-begin:cpt-pstorageuihci-algo-create-fetch-make-request:p1:inst-server-error
        const serverError = new InternalServerError(response, data);
        // @cpt-end:cpt-pstorageuihci-algo-create-fetch-make-request:p1:inst-server-error
        // @cpt-begin:cpt-pstorageuihci-algo-create-fetch-make-request:p1:inst-track-5xx
        trackBackendError(serverError, response.status, url, data?.error?.err_code, {
          request_method: restOptions.method,
          response_data: data,
          error_category: '5xx'
        });
        // @cpt-end:cpt-pstorageuihci-algo-create-fetch-make-request:p1:inst-track-5xx
        // @cpt-begin:cpt-pstorageuihci-algo-create-fetch-make-request:p1:inst-throw-5xx
        throw serverError;
        // @cpt-end:cpt-pstorageuihci-algo-create-fetch-make-request:p1:inst-throw-5xx
        // @cpt-end:cpt-pstorageuihci-state-create-fetch-request-lifecycle:p1:inst-to-server-failed
        // @cpt-end:cpt-pstorageuihci-algo-create-fetch-make-request:p1:inst-server-branch
      });
  }

  // @cpt-algo:cpt-pstorageuihci-algo-create-fetch-make-fetch:p1
  makeFetch({ url, headers, path, params, abortController, ...options }: Config): Promise<Response> {
    // @cpt-begin:cpt-pstorageuihci-algo-create-fetch-make-fetch:p1:inst-register-controller
    const requestController = this.createRequestController(abortController);
    // @cpt-end:cpt-pstorageuihci-algo-create-fetch-make-fetch:p1:inst-register-controller

    // @cpt-begin:cpt-pstorageuihci-algo-create-fetch-make-fetch:p1:inst-fetch
    return fetch(url, {
      mode: 'cors',
      credentials: 'same-origin',
      ...options,
      headers,
      signal: requestController.controller.signal
    })
      // @cpt-end:cpt-pstorageuihci-algo-create-fetch-make-fetch:p1:inst-fetch
      // @cpt-begin:cpt-pstorageuihci-algo-create-fetch-make-fetch:p1:inst-remove-controller
      .finally(() => {
        this.removeController(requestController);
      });
    // @cpt-end:cpt-pstorageuihci-algo-create-fetch-make-fetch:p1:inst-remove-controller
  }

  // @cpt-algo:cpt-pstorageuihci-algo-create-fetch-verb-shortcuts:p1
  // @cpt-dod:cpt-pstorageuihci-dod-create-fetch-slow-request-logging:p1
  get<T = any>(path: UriPath, params?: FetchRequestParams, options?: any): Promise<FetchResponse<T>> {
    // @cpt-begin:cpt-pstorageuihci-algo-create-fetch-verb-shortcuts:p1:inst-get-url
    const rootPath = (options?.rootPath || rootDir).replace(/\/$/, '');
    const url = this.constructUri(path, rootPath, params || {});
    // @cpt-end:cpt-pstorageuihci-algo-create-fetch-verb-shortcuts:p1:inst-get-url

    // @cpt-begin:cpt-pstorageuihci-algo-create-fetch-verb-shortcuts:p1:inst-slow-start
    const slowRequestsStore = useSlowRequestsStore();

    const stopRequestLog = slowRequestsStore.logSlowRequest({ url, method: 'GET' });
    // @cpt-end:cpt-pstorageuihci-algo-create-fetch-verb-shortcuts:p1:inst-slow-start

    // @cpt-begin:cpt-pstorageuihci-algo-create-fetch-verb-shortcuts:p1:inst-get-call
    return this.makeRequest(path, { ...options, params, method: 'get' })
      // @cpt-end:cpt-pstorageuihci-algo-create-fetch-verb-shortcuts:p1:inst-get-call
      .then((result) => {
        // @cpt-begin:cpt-pstorageuihci-algo-create-fetch-verb-shortcuts:p1:inst-slow-completed
        stopRequestLog('completed');
        return result;
        // @cpt-end:cpt-pstorageuihci-algo-create-fetch-verb-shortcuts:p1:inst-slow-completed
      })
      .catch((err) => {
        // @cpt-begin:cpt-pstorageuihci-algo-create-fetch-verb-shortcuts:p1:inst-get-catch
        // @cpt-begin:cpt-pstorageuihci-algo-create-fetch-verb-shortcuts:p1:inst-status-failed
        let status: 'canceled' | 'network_error' | 'failed' = 'failed';
        // @cpt-end:cpt-pstorageuihci-algo-create-fetch-verb-shortcuts:p1:inst-status-failed

        // @cpt-begin:cpt-pstorageuihci-algo-create-fetch-verb-shortcuts:p1:inst-status-canceled
        if (err.name === 'AbortError' || err instanceof CancellationError) {
          status = 'canceled';
        // @cpt-end:cpt-pstorageuihci-algo-create-fetch-verb-shortcuts:p1:inst-status-canceled
        // @cpt-begin:cpt-pstorageuihci-algo-create-fetch-verb-shortcuts:p1:inst-status-network
        } else if (err instanceof NetworkError) {
          status = 'network_error';
        }
        // @cpt-end:cpt-pstorageuihci-algo-create-fetch-verb-shortcuts:p1:inst-status-network

        // @cpt-begin:cpt-pstorageuihci-algo-create-fetch-verb-shortcuts:p1:inst-slow-fail
        stopRequestLog(status, err);

        throw err;
        // @cpt-end:cpt-pstorageuihci-algo-create-fetch-verb-shortcuts:p1:inst-slow-fail
        // @cpt-end:cpt-pstorageuihci-algo-create-fetch-verb-shortcuts:p1:inst-get-catch
      });
  }

  buildBodyPayload(body?: FetchRequestBody): { body?: string } {
    return isNil(body) ? {} : { body: JSON.stringify(body) };
  }

  post<T = any>(path: UriPath, body?: FetchRequestBody, options?: any): Promise<FetchResponse<T>> {
    // @cpt-begin:cpt-pstorageuihci-algo-create-fetch-verb-shortcuts:p1:inst-mutating-verb
    return this.makeRequest(path, { ...options, ...this.buildBodyPayload(body), method: 'post' });
    // @cpt-end:cpt-pstorageuihci-algo-create-fetch-verb-shortcuts:p1:inst-mutating-verb
  }

  put<T = any>(path: UriPath, body?: FetchRequestBody, options?: any): Promise<FetchResponse<T>> {
    // @cpt-begin:cpt-pstorageuihci-algo-create-fetch-verb-shortcuts:p1:inst-mutating-verb
    return this.makeRequest(path, { ...options, ...this.buildBodyPayload(body), method: 'put' });
    // @cpt-end:cpt-pstorageuihci-algo-create-fetch-verb-shortcuts:p1:inst-mutating-verb
  }

  patch<T = any>(path: UriPath, body?: FetchRequestBody, options?: any): Promise<FetchResponse<T>> {
    // @cpt-begin:cpt-pstorageuihci-algo-create-fetch-verb-shortcuts:p1:inst-mutating-verb
    return this.makeRequest(path, { ...options, ...this.buildBodyPayload(body), method: 'PATCH' });
    // @cpt-end:cpt-pstorageuihci-algo-create-fetch-verb-shortcuts:p1:inst-mutating-verb
  }

  delete<T = any>(path: UriPath, body?: FetchRequestBody, options?: any): Promise<FetchResponse<T>> {
    // @cpt-begin:cpt-pstorageuihci-algo-create-fetch-verb-shortcuts:p1:inst-mutating-verb
    return this.makeRequest(path, { ...options, ...this.buildBodyPayload(body), method: 'delete' });
    // @cpt-end:cpt-pstorageuihci-algo-create-fetch-verb-shortcuts:p1:inst-mutating-verb
  }

  // @cpt-algo:cpt-pstorageuihci-algo-create-fetch-upload:p1
  // @cpt-state:cpt-pstorageuihci-state-create-fetch-upload-lifecycle:p1
  upload(path: UriPath, body: (Document | XMLHttpRequestBodyInit | null), options: any, onProgress?: (process: ProgressEvent, xhr: XMLHttpRequest) => void): Promise<UploadResponse> {
    const rootPath: string = rootDir.replace(/\/$/, '');

    return new Promise((resolve, reject) => {
      // @cpt-begin:cpt-pstorageuihci-algo-create-fetch-upload:p1:inst-xhr-init
      const xhr: XMLHttpRequest = new XMLHttpRequest();
      xhr.responseType = 'text';
      // @cpt-end:cpt-pstorageuihci-algo-create-fetch-upload:p1:inst-xhr-init
      // @cpt-begin:cpt-pstorageuihci-algo-create-fetch-upload:p1:inst-xhr-open
      xhr.open(options.method, rootPath + path);
      xhr.timeout = 0;
      // @cpt-end:cpt-pstorageuihci-algo-create-fetch-upload:p1:inst-xhr-open

      // @cpt-begin:cpt-pstorageuihci-algo-create-fetch-upload:p1:inst-extract-file
      const { fileName, fileSize } = extractFileInfo(body, options);
      // @cpt-end:cpt-pstorageuihci-algo-create-fetch-upload:p1:inst-extract-file
      // @cpt-begin:cpt-pstorageuihci-algo-create-fetch-upload:p1:inst-track-start
      // @cpt-begin:cpt-pstorageuihci-state-create-fetch-upload-lifecycle:p1:inst-upload-start
      const uploadStartTime = trackFileUploadStart({
        fileName,
        fileSize,
        metadata: {
          upload_path: path,
          request_method: options.method
        }
      });
      // @cpt-end:cpt-pstorageuihci-state-create-fetch-upload-lifecycle:p1:inst-upload-start
      // @cpt-end:cpt-pstorageuihci-algo-create-fetch-upload:p1:inst-track-start

      // @cpt-begin:cpt-pstorageuihci-algo-create-fetch-upload:p1:inst-set-headers
      const headers: XAuthorizationHeaders & FetchHeaders = {
        'X-Requested-With': 'XMLHttpRequest',
        ...options.headers,
        ...this.getAuthorizationHeaders(options),
      };

      Object.keys(headers).forEach(key => xhr.setRequestHeader(key, headers[key]));
      // @cpt-end:cpt-pstorageuihci-algo-create-fetch-upload:p1:inst-set-headers

      // @cpt-begin:cpt-pstorageuihci-algo-create-fetch-upload:p1:inst-on-load
      xhr.onload = async (e: ProgressEvent) => {
        const response: XMLHttpRequest = e.target as XMLHttpRequest;

        // @cpt-begin:cpt-pstorageuihci-algo-create-fetch-upload:p1:inst-parse-upload
        const { responseText } = response;
        let responseBody: any = {};
        const contentType: string = response.getResponseHeader('content-type') ?? '';
        if (responseText.length && /application\/json/i.test(contentType)) {
          try {
            responseBody = JSON.parse(responseText);
          } catch (err) {
            reject(new JsonParseError(responseText, response.status));
          }
        }
        // @cpt-end:cpt-pstorageuihci-algo-create-fetch-upload:p1:inst-parse-upload

        // @cpt-begin:cpt-pstorageuihci-algo-create-fetch-upload:p1:inst-build-adapter
        const responseAdapter: Response = {
          status: response.status,
          statusText: response.statusText,
          headers: {
            get: (name: string) => response.getResponseHeader(name)
          }
        } as Response;
        // @cpt-end:cpt-pstorageuihci-algo-create-fetch-upload:p1:inst-build-adapter

        // @cpt-begin:cpt-pstorageuihci-algo-create-fetch-upload:p1:inst-upload-auth
        this.authGuard(responseAdapter, responseBody, options as AuthGuardParams).catch((err: Error) => reject(err));
        // @cpt-end:cpt-pstorageuihci-algo-create-fetch-upload:p1:inst-upload-auth

        const result: UploadResponse = { data: responseBody, response };
        if (response.status >= 200 && response.status < 400) {
          // @cpt-begin:cpt-pstorageuihci-algo-create-fetch-upload:p1:inst-upload-success
          // @cpt-begin:cpt-pstorageuihci-state-create-fetch-upload-lifecycle:p1:inst-upload-ok
          // @cpt-begin:cpt-pstorageuihci-algo-create-fetch-upload:p1:inst-track-success
          trackFileUploadComplete({
            startTime: uploadStartTime,
            fileName,
            fileSize,
            success: true,
            additionalProperties: {
              status_code: response.status,
              response_size: responseText.length
            }
          });
          // @cpt-end:cpt-pstorageuihci-algo-create-fetch-upload:p1:inst-track-success
          // @cpt-begin:cpt-pstorageuihci-algo-create-fetch-upload:p1:inst-resolve-upload
          bus.emit(responseBody);
          resolve(result);
          // @cpt-end:cpt-pstorageuihci-algo-create-fetch-upload:p1:inst-resolve-upload
          // @cpt-end:cpt-pstorageuihci-state-create-fetch-upload-lifecycle:p1:inst-upload-ok
          // @cpt-end:cpt-pstorageuihci-algo-create-fetch-upload:p1:inst-upload-success
        } else if (response.status >= 400 && response.status < 500) {
          // @cpt-begin:cpt-pstorageuihci-algo-create-fetch-upload:p1:inst-upload-4xx
          // @cpt-begin:cpt-pstorageuihci-state-create-fetch-upload-lifecycle:p1:inst-upload-rejected
          const errorMessage = `HTTP ${response.status}`;
          trackFileUploadComplete({
            startTime: uploadStartTime,
            fileName,
            fileSize,
            success: false,
            errorMessage,
            additionalProperties: {
              status_code: response.status,
              error_category: '4xx'
            }
          });
          reject(new ValidationError(response, responseBody));
          // @cpt-end:cpt-pstorageuihci-state-create-fetch-upload-lifecycle:p1:inst-upload-rejected
          // @cpt-end:cpt-pstorageuihci-algo-create-fetch-upload:p1:inst-upload-4xx
        } else {
          // @cpt-begin:cpt-pstorageuihci-algo-create-fetch-upload:p1:inst-upload-5xx
          // @cpt-begin:cpt-pstorageuihci-state-create-fetch-upload-lifecycle:p1:inst-upload-failed
          const errorMessage = `HTTP ${response.status}`;
          trackFileUploadComplete({
            startTime: uploadStartTime,
            fileName,
            fileSize,
            success: false,
            errorMessage,
            additionalProperties: {
              status_code: response.status,
              error_category: '5xx'
            }
          });
          reject(new ServerError(response, responseBody));
          // @cpt-end:cpt-pstorageuihci-state-create-fetch-upload-lifecycle:p1:inst-upload-failed
          // @cpt-end:cpt-pstorageuihci-algo-create-fetch-upload:p1:inst-upload-5xx
        }
      };
      // @cpt-end:cpt-pstorageuihci-algo-create-fetch-upload:p1:inst-on-load

      let timeoutId: ReturnType<typeof setTimeout>;
      // @cpt-begin:cpt-pstorageuihci-algo-create-fetch-upload:p1:inst-on-error
      // @cpt-begin:cpt-pstorageuihci-state-create-fetch-upload-lifecycle:p1:inst-upload-failed
      const onError = (err) => {
        clearTimeout(timeoutId);
        trackFileUploadComplete({
          startTime: uploadStartTime,
          fileName,
          fileSize,
          success: false,
          errorMessage: 'Network error',
          additionalProperties: {
            error_type: 'network_error'
          }
        });
        reject(new NetworkError(err));
      };
      // @cpt-end:cpt-pstorageuihci-state-create-fetch-upload-lifecycle:p1:inst-upload-failed
      // @cpt-end:cpt-pstorageuihci-algo-create-fetch-upload:p1:inst-on-error

      // @cpt-begin:cpt-pstorageuihci-algo-create-fetch-upload:p1:inst-on-abort
      // @cpt-begin:cpt-pstorageuihci-state-create-fetch-upload-lifecycle:p1:inst-upload-canceled
      const onAbort = (err) => {
        clearTimeout(timeoutId);
        trackFileUploadComplete({
          startTime: uploadStartTime,
          fileName,
          fileSize,
          success: false,
          errorMessage: 'Upload cancelled',
          additionalProperties: {
            error_type: 'cancellation'
          }
        });
        reject(new CancellationError(err));
      };
      // @cpt-end:cpt-pstorageuihci-state-create-fetch-upload-lifecycle:p1:inst-upload-canceled
      // @cpt-end:cpt-pstorageuihci-algo-create-fetch-upload:p1:inst-on-abort

      xhr.onerror = onError;
      xhr.onabort = onAbort;

      // @cpt-begin:cpt-pstorageuihci-algo-create-fetch-upload:p1:inst-progress-branch
      if (xhr.upload && onProgress) {
        // @cpt-begin:cpt-pstorageuihci-algo-create-fetch-upload:p1:inst-progress-throttle
        xhr.upload.onprogress = throttle((progress: ProgressEvent) => {
          clearTimeout(timeoutId);
          // @cpt-begin:cpt-pstorageuihci-state-create-fetch-upload-lifecycle:p1:inst-upload-timeout
          timeoutId = setTimeout(() => {
            trackFileUploadComplete({
              startTime: uploadStartTime,
              fileName,
              fileSize,
              success: false,
              errorMessage: 'Upload timeout',
              additionalProperties: {
                error_type: 'timeout',
                timeout_ms: options.uploadTimeout || 60000
              }
            });
            onError(progress);
          }, options.uploadTimeout || 60000);
          // @cpt-end:cpt-pstorageuihci-state-create-fetch-upload-lifecycle:p1:inst-upload-timeout
          // @cpt-end:cpt-pstorageuihci-algo-create-fetch-upload:p1:inst-progress-throttle
          // @cpt-begin:cpt-pstorageuihci-algo-create-fetch-upload:p1:inst-progress-cb
          onProgress(progress, xhr);
          // @cpt-end:cpt-pstorageuihci-algo-create-fetch-upload:p1:inst-progress-cb
        }, 100);
        // @cpt-begin:cpt-pstorageuihci-algo-create-fetch-upload:p1:inst-clear-timeout
        xhr.onloadend = () => {
          clearTimeout(timeoutId);
        };
        // @cpt-end:cpt-pstorageuihci-algo-create-fetch-upload:p1:inst-clear-timeout
      }
      // @cpt-end:cpt-pstorageuihci-algo-create-fetch-upload:p1:inst-progress-branch

      // @cpt-begin:cpt-pstorageuihci-algo-create-fetch-upload:p1:inst-xhr-send
      xhr.send(body);
      // @cpt-end:cpt-pstorageuihci-algo-create-fetch-upload:p1:inst-xhr-send
    });
  }
}
